import { useNavigate } from "@tanstack/react-router";
import { Link } from "@corpuscomplete/web/components/link";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { List } from "@corpuscomplete/core/common/list";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@corpuscomplete/web-ui/components/empty";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { createId, must, nSortIndexesBetween } from "@corpuscomplete/core/utils";
import { ListProgress } from "@corpuscomplete/web/modules/common/list/components/list-progress";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { ListChecksIcon, PlusIcon } from "lucide-react";

export function ListsPage() {
  const z = useZero();
  const [lists, listsDetails] = useQuery(queries.list.all());
  const [defaultCollection, defaultCollectionDetails] = useQuery(
    queries.listCollection.myDefault(),
  );
  const navigate = useNavigate();
  if (listsDetails.type === "unknown" || defaultCollectionDetails.type === "unknown") {
    return null;
  }

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Text className="mr-auto">Lists</Text>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              let collectionId: string;
              if (defaultCollection) {
                collectionId = defaultCollection.id;
              } else {
                collectionId = createId();
                z.mutate(
                  mutators.listCollection.createMyDefault({
                    id: collectionId,
                  }),
                );
              }
              const input = createTestList(collectionId);
              const { client } = z.mutate(mutators.list.create(input));
              void client.then((result) => {
                if (result.type === "success") {
                  void navigate({
                    to: "/personal/lists/$id",
                    params: { id: input.id },
                  });
                }
              });
            }}
          >
            Create List
          </Button>
        </PageHeader>
        <PageContent>
          {lists.length === 0 ? (
            <Empty className="flex-1">
              <EmptyHeader>
                <EmptyMedia>
                  <div className="flex size-16 items-center justify-center rounded-full bg-muted">
                    <ListChecksIcon className="size-8 text-muted-foreground" />
                  </div>
                </EmptyMedia>
                <EmptyTitle>No lists yet</EmptyTitle>
                <EmptyDescription>
                  Create a list to organize your shopping, tasks, or anything else.
                </EmptyDescription>
              </EmptyHeader>
              <Button
                onClick={() => {
                  let collectionId: string;
                  if (defaultCollection) {
                    collectionId = defaultCollection.id;
                  } else {
                    collectionId = createId();
                    z.mutate(
                      mutators.listCollection.createMyDefault({
                        id: collectionId,
                      }),
                    );
                  }
                  const input = createTestList(collectionId);
                  z.mutate(mutators.list.create(input));
                  void navigate({
                    to: "/personal/lists/$id",
                    params: { id: input.id },
                  });
                }}
              >
                <PlusIcon className="mr-1.5 size-4" />
                Create Your First List
              </Button>
            </Empty>
          ) : (
            <div className="flex flex-col">
              {lists.map((list) => (
                <Link
                  to="/personal/lists/$id"
                  params={{ id: list.id }}
                  key={list.id}
                  className="flex flex-row items-center gap-2 p-2 hover:cursor-default hover:bg-muted"
                >
                  <ListProgress list={list} size="sm" />
                  <Text>{list.title}</Text>
                </Link>
              ))}
            </div>
          )}
        </PageContent>
      </PageBody>
    </Page>
  );
}

function createTestList(collectionId: string): List.Create {
  const id = createId();
  const section1 = createId();
  const section2 = createId();
  const sectionSortIndexes = nSortIndexesBetween(null, null, 2);
  const section1ItemSortIndexes = nSortIndexesBetween(null, null, 3);
  const section2ItemSortIndexes = nSortIndexesBetween(null, null, 2);
  const orphanItemSortIndexes = nSortIndexesBetween(null, null, 3);
  return {
    id,
    collectionId,
    title: "New List",
    type: "shopping",
    sections: [
      {
        id: section1,
        title: "New Section 1",
        sortIndex: must(sectionSortIndexes[0]),
        items: [
          {
            id: createId(),
            label: "New Item 1.1",
            sortIndex: must(section1ItemSortIndexes[0]),
          },
          {
            id: createId(),
            label: "New Item 1.2",
            sortIndex: must(section1ItemSortIndexes[1]),
          },
          {
            id: createId(),
            label: "New Item 1.3",
            sortIndex: must(section1ItemSortIndexes[2]),
          },
        ],
      },
      {
        id: section2,
        title: "New Section 2",
        sortIndex: must(sectionSortIndexes[1]),
        items: [
          {
            id: createId(),
            label: "New Item 2.1",
            sortIndex: must(section2ItemSortIndexes[0]),
          },
          {
            id: createId(),
            label: "New Item 2.2",
            sortIndex: must(section2ItemSortIndexes[1]),
          },
        ],
      },
    ],
    orphanItems: [
      {
        id: createId(),
        label: "New Item X",
        sortIndex: must(orphanItemSortIndexes[0]),
      },
      {
        id: createId(),
        label: "New Item Y",
        sortIndex: must(orphanItemSortIndexes[1]),
      },
      {
        id: createId(),
        label: "New Item Z",
        sortIndex: must(orphanItemSortIndexes[2]),
      },
    ],
  };
}
